import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { fetchFeedbackDashboard, fetchFeedbackItem } from "@/lib/api";
import {
  Compass as CompassIcon, Loader2, AlertTriangle, Lightbulb, X, RefreshCw, MessageSquareText, ImageOff,
} from "lucide-react";

function StatCard({ label, value, tone }) {
  return (
    <div className={`rounded-sm border px-4 py-3 ${tone}`}>
      <div className="text-[26px] font-serif-display leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-mono-panel mt-1 opacity-70">{label}</div>
    </div>
  );
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div className="mb-3">
      <p className="text-[10px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel mb-0.5">{label}</p>
      <div className="text-[13.5px] text-stone-800 whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function DetailModal({ id, onClose }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    fetchFeedbackItem(id).then(setItem).catch((e) => setError(e.response?.data?.detail || "Could not load."));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/40 backdrop-blur-sm px-4 py-8 overflow-y-auto" onClick={onClose} data-testid="feedback-detail-modal">
      <div className="w-full max-w-3xl bg-[#fdfcfa] border border-stone-200 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h3 className="font-serif-display text-lg text-stone-900">Feedback detail</h3>
          <button onClick={onClose} data-testid="feedback-detail-close" className="text-stone-400 hover:text-stone-700"><X className="h-5 w-5" /></button>
        </div>
        {!item && !error && <div className="p-8 flex items-center gap-2 text-stone-400 text-[14px]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {error && <div className="p-8 text-red-600 text-[14px]">{error}</div>}
        {item && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            <div>
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono-panel uppercase tracking-[0.14em] px-2 py-0.5 rounded-sm mb-3 ${item.feedback_type === "problem" ? "bg-amber-50 text-amber-800" : "bg-[#8C3A2A]/[0.07] text-[#8C3A2A]"}`}>
                {item.feedback_type === "problem" ? <AlertTriangle className="h-3 w-3" /> : <Lightbulb className="h-3 w-3" />}
                {item.feedback_type === "problem" ? "Problem" : "Suggestion"}
              </span>
              <Field label="Comment">{item.comment}</Field>
              <Field label="Stage">{item.stage}</Field>
              <Field label="Coaching target">{item.coaching_target}</Field>
              <Field label="Assignment">{item.assignment}</Field>
              <Field label="Paragraph snapshot">{item.paragraph_snapshot}</Field>
              <Field label="Last The Writing Studio message">{item.last_compass_message}</Field>
              <Field label="Last learner message">{item.last_learner_message}</Field>
              <Field label="User">{item.user_email || item.user_id || "Anonymous"}</Field>
              <Field label="Session">{item.session_id}</Field>
              <Field label="When">{item.created_at}</Field>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] text-stone-400 font-mono-panel mb-1.5">Screenshot</p>
              {item.screenshot ? (
                <img src={item.screenshot} alt="feedback screenshot" data-testid="feedback-detail-screenshot" className="w-full rounded-sm border border-stone-200" />
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-stone-400 border border-dashed border-stone-300 rounded-sm py-12 text-[13px]">
                  <ImageOff className="h-6 w-6" /> No screenshot captured
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FeedbackDashboard() {
  const { user, isAuthed } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(""); // "" | "problem" | "suggestion"
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    setError("");
    setData(null);
    fetchFeedbackDashboard(filter || undefined)
      .then(setData)
      .catch((e) => setError(e.response?.status === 403 ? "You need an admin account to view this dashboard." : (e.response?.data?.detail || "Could not load feedback.")));
  }, [filter]);

  useEffect(() => {
    if (user === undefined) return;
    if (!isAuthed) { setAuthOpen(true); return; }
    load();
  }, [user, isAuthed, load]);

  if (user === undefined) {
    return <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6" data-testid="feedback-dashboard-signin">
        <div className="text-center">
          <CompassIcon className="h-6 w-6 text-[#8C3A2A] mx-auto mb-3" />
          <h1 className="font-serif-display text-2xl text-stone-900 mb-1">Teacher Review</h1>
          <p className="text-[14px] text-stone-500 mb-4">Sign in with an admin account to view learner feedback.</p>
          <button onClick={() => setAuthOpen(true)} className="bg-[#8C3A2A] text-white text-[13.5px] px-5 py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors">Sign in</button>
        </div>
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} reason="Sign in with an admin account to review feedback." onSuccess={load} />
      </div>
    );
  }

  const counts = data?.counts || { total: 0, problems: 0, suggestions: 0 };
  const items = data?.items || [];

  const filterBtn = (val, label) => (
    <button
      onClick={() => setFilter(val)}
      data-testid={`feedback-filter-${val || "all"}`}
      className={`text-[12.5px] px-3 py-1.5 rounded-sm border transition-colors ${filter === val ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-[#8C3A2A]" : "border-stone-200 text-stone-500 hover:border-stone-400"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#faf9f6] px-6 py-8" data-testid="feedback-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 text-[#8C3A2A]" />
            <h1 className="font-serif-display text-2xl text-stone-900">Teacher Review — Feedback</h1>
          </div>
          <button onClick={load} data-testid="feedback-refresh" className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard label="Total" value={counts.total} tone="border-stone-200 text-stone-800" />
          <StatCard label="Problems" value={counts.problems} tone="border-amber-200 bg-amber-50 text-amber-800" />
          <StatCard label="Suggestions" value={counts.suggestions} tone="border-[#8C3A2A]/20 bg-[#8C3A2A]/[0.04] text-[#8C3A2A]" />
        </div>

        <div className="flex gap-2 mb-4">
          {filterBtn("", "All")}
          {filterBtn("problem", "Problems")}
          {filterBtn("suggestion", "Suggestions")}
        </div>

        {error && <p className="text-[14px] text-red-600" data-testid="feedback-dashboard-error">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-stone-400 text-[14px]"><Loader2 className="h-4 w-4 animate-spin" /> Loading feedback…</div>}

        {data && items.length === 0 && (
          <div className="border border-dashed border-stone-300 rounded-sm p-10 text-center text-stone-500 text-[14px]" data-testid="feedback-dashboard-empty">
            No feedback yet.
          </div>
        )}

        {items.length > 0 && (
          <ul className="space-y-2" data-testid="feedback-dashboard-list">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`feedback-row-${it.id}`}
                onClick={() => setDetailId(it.id)}
                className="cursor-pointer border border-stone-200 rounded-sm bg-white px-4 py-3 hover:border-[#8C3A2A]/40 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <span className={`shrink-0 mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono-panel uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm ${it.feedback_type === "problem" ? "bg-amber-50 text-amber-800" : "bg-[#8C3A2A]/[0.07] text-[#8C3A2A]"}`}>
                    {it.feedback_type === "problem" ? <AlertTriangle className="h-3 w-3" /> : <Lightbulb className="h-3 w-3" />}
                    {it.feedback_type === "problem" ? "Problem" : "Idea"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] text-stone-800 line-clamp-2">{it.comment || <span className="italic text-stone-400">(no comment)</span>}</p>
                    <p className="text-[11.5px] text-stone-400 mt-1 truncate">
                      {[it.stage, it.coaching_target, it.user_email || it.user_id || "anonymous"].filter(Boolean).join(" · ")}
                      {it.created_at ? ` · ${new Date(it.created_at).toLocaleString()}` : ""}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
